import "./logotype-block.scss";
import KeenSlider, { KeenSliderInstance } from "keen-slider";
import { autoScrollAnimation } from "helpers/sliderConfig";

const CLONE_ATTR = "data-logo-clone";

type LogotypeBlockElement = HTMLElement & {
    _keenAutoScroll?: KeenSliderInstance;
    _isPaused?: boolean;
    _hoverListeners?: { enter: () => void; leave: () => void };
};

(function (): void {
    const blocks = document.querySelectorAll<LogotypeBlockElement>(".js-logotype-block");
    if (blocks.length === 0) return;

    // `md` breakpoint (896 px) — matches @include breakpoint(md) in _breakpoints.scss
    const TABLET_BREAKPOINT = 896;
    // mobile (<896 px) always scrolls; tablet/desktop scrolls only when logo count exceeds this
    const SCROLL_THRESHOLD = 5;

    const shouldScroll = (root: LogotypeBlockElement): boolean => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
        const count = Number.parseInt(root.dataset.logoCount ?? "0", 10);
        if (window.innerWidth < TABLET_BREAKPOINT) return true;
        return count > SCROLL_THRESHOLD;
    };

    const SLIDE_MIN_WIDTH = 160; // must match .is-scrolling figure { min-width } in SCSS
    const SLIDE_SPACING = 48; // must match keen-slider slides.spacing option

    const cloneSlides = (track: HTMLElement, originals: HTMLElement[]): void => {
        if (originals.length === 0) return;
        const slideWidth = SLIDE_MIN_WIDTH + SLIDE_SPACING;
        // On mobile/tablet the originals already fill the viewport enough for the loop
        if (originals.length * slideWidth >= window.innerWidth * 2) return;
        // On wider screens, 7+ logos fill the viewport adequately without clones
        if (originals.length > 6) return;
        const perView = Math.ceil(window.innerWidth / slideWidth);
        // Clones must exceed perView so an original and its clone are never simultaneously visible
        const needed = Math.max(
            Math.ceil((window.innerWidth * 2.5) / slideWidth),
            originals.length + perView + 1,
        );
        // Round up to a full multiple of originals so every logo appears equally often in the loop
        const rawClones = Math.max(needed - originals.length, 0);
        const clonesCount = Math.ceil(rawClones / originals.length) * originals.length;
        for (let i = 0; i < clonesCount; i++) {
            const clone = originals[i % originals.length].cloneNode(true) as HTMLElement;
            clone.setAttribute(CLONE_ATTR, "");
            clone.setAttribute("aria-hidden", "true");
            track.appendChild(clone);
        }
    };

    const removeClones = (track: HTMLElement): void => {
        track.querySelectorAll<HTMLElement>(`[${CLONE_ATTR}]`).forEach((el) => el.remove());
    };

    const startScroll = (s: KeenSliderInstance): void => {
        if (!s.track.details) return;
        s.moveToIdx(s.track.details.abs + 5, true, autoScrollAnimation);
    };

    const pauseScroll = (root: LogotypeBlockElement): void => {
        root._isPaused = true;
        root._keenAutoScroll?.animator.stop();
    };

    const resumeScroll = (root: LogotypeBlockElement): void => {
        root._isPaused = false;
        const s = root._keenAutoScroll;
        if (s) startScroll(s);
    };

    const initAutoScroll = (root: LogotypeBlockElement): void => {
        const track = root.querySelector<HTMLElement>(".logo-slider__track");
        if (!track) return;

        const originals = Array.from(track.querySelectorAll<HTMLElement>(`figure:not([${CLONE_ATTR}])`));
        if (originals.length === 0) return;

        cloneSlides(track, originals);

        const allSlides = Array.from(track.querySelectorAll<HTMLElement>("figure"));
        track.classList.add("keen-slider");
        allSlides.forEach((fig) => fig.classList.add("keen-slider__slide"));
        root.classList.add("is-scrolling");
        root._isPaused = false;

        root._keenAutoScroll = new KeenSlider(track, {
            loop: true,
            renderMode: "performance",
            drag: true,
            slides: { perView: "auto", spacing: 48 },
            created(s) {
                s.moveToIdx(5, true, autoScrollAnimation);
            },
            updated(s) {
                if (!root._isPaused) startScroll(s);
            },
            animationEnded(s) {
                if (!root._isPaused) startScroll(s);
            },
            dragStarted() {
                pauseScroll(root);
            },
            dragEnded() {
                resumeScroll(root);
            },
        });

        const onEnter = (): void => pauseScroll(root);
        const onLeave = (): void => resumeScroll(root);
        track.addEventListener("mouseenter", onEnter);
        track.addEventListener("mouseleave", onLeave);
        root._hoverListeners = { enter: onEnter, leave: onLeave };
    };

    const destroyAutoScroll = (root: LogotypeBlockElement): void => {
        const track = root.querySelector<HTMLElement>(".logo-slider__track");

        if (track && root._hoverListeners) {
            track.removeEventListener("mouseenter", root._hoverListeners.enter);
            track.removeEventListener("mouseleave", root._hoverListeners.leave);
        }
        delete root._hoverListeners;
        delete root._isPaused;

        root._keenAutoScroll?.destroy();
        delete root._keenAutoScroll;

        if (track) {
            removeClones(track);
            track.classList.remove("keen-slider");
            track.style.removeProperty("transform");
            track.style.removeProperty("width");
            track.style.removeProperty("min-width");
            track.style.removeProperty("max-width");

            track.querySelectorAll<HTMLElement>("figure").forEach((fig) => {
                fig.classList.remove("keen-slider__slide");
                fig.style.removeProperty("transform");
                fig.style.removeProperty("width");
                fig.style.removeProperty("min-width");
                fig.style.removeProperty("max-width");
            });
        }

        root.classList.remove("is-scrolling");
    };

    const reconcile = (root: LogotypeBlockElement): void => {
        const desired = shouldScroll(root);
        const active = !!root._keenAutoScroll;
        if (desired && !active) initAutoScroll(root);
        if (!desired && active) destroyAutoScroll(root);
    };

    let resizeTimer: ReturnType<typeof setTimeout>;
    const onResize = (): void => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            blocks.forEach(reconcile);
        }, 150);
    };

    blocks.forEach(reconcile);
    window.addEventListener("resize", onResize);
})();